import siteOperationTypeBadgesStyles from './site-operation-type-badges.styles.css?inline';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ApiFactory } from '../../../api/agwasuite-api-factory';
import { SecurityApi } from '../../../api/security/security-api';
import type {
  LoginSiteDomainScopeGrant,
  SecurityDomainDto,
} from '../../../api/agwa-api-model';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import {
  collapseOperationTypeCodes,
  getOrganizationTypeLabel,
} from '../shared/organization-type-labels';

import '@shoelace-style/shoelace/dist/components/tooltip/tooltip.js';

/**
 * What this site IS, as short codes, immediately before its name on every tab.
 *
 * WHY CODES RATHER THAN NAMES. "Prime System Supplier" and "Prime System Manufacturer" differ by
 * one word in the middle and wrap on a narrow header; PSS and PSM do not, and the people who use
 * this software know them. The strip is an identifier, not an explanation - the explanation lives
 * one click away in the operation-type sheet on the Overview tab.
 *
 * AT THE TITLE'S OWN SIZE, ahead of the name. It was smaller and on a line of its own first, which
 * made the thing that identifies a site the quietest element in its header. "PGM Test Site 1c"
 * reads as one identity.
 *
 * DEACTIVATED REGISTRATIONS ARE ABSENT, not greyed. The strip answers "what is this site", and a
 * deactivated type is what it WAS; showing it here would make the site's identity ambiguous in the
 * one place that exists to make it instant.
 *
 * The codes come from the server (`SecurityDomainDto.shortCode`) rather than being derived from the
 * domain code here, because they are AGWA's own vocabulary and a second copy would be free to drift.
 */
@customElement('site-operation-type-badges')
export class SiteOperationTypeBadges extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteOperationTypeBadgesStyles)];

  /** The site's own scope grants, threaded from the workspace store so the strip follows a change. */
  @property({ attribute: false }) domains: LoginSiteDomainScopeGrant[] = [];

  @state() private catalogue: SecurityDomainDto[] = [];

  connectedCallback() {
    super.connectedCallback();
    void this.loadCatalogue();
  }

  /**
   * The domain catalogue, for the code-to-short-code map.
   *
   * Failure is silent by design: this strip is an identifier beside a heading, and a site page that
   * refused to render because a supplementary lookup failed would trade a missing badge for a
   * missing page.
   */
  private async loadCatalogue() {
    try {
      const response =
        await ApiFactory.authenticated(SecurityApi).getSecurityDomains();
      if (response.success) {
        this.catalogue = response.data?.domains ?? [];
      }
    } catch {
      this.catalogue = [];
    }
  }

  private get badges(): Array<{
    code: string;
    shortCode: string;
    label: string;
  }> {
    if (this.catalogue.length === 0) return [];

    // The collapse applies before anything is rendered, so a covered code never reaches the
    // strip. It is the one shared rule (PSS collapses to PSM anywhere it is shown); this strip
    // carried a private copy of it until 2026-09-17.
    const activeCodes = new Set(
      collapseOperationTypeCodes(
        this.domains
          .filter((domain) => domain.active && domain.domainCode)
          .map((domain) => domain.domainCode)
      ).map((code) => code.toLowerCase())
    );

    // Catalogue order, which is the server's DisplayOrder - the same order the sheet lists them in,
    // so the two never disagree about which comes first.
    return this.catalogue
      .filter(
        (domain) =>
          domain.code &&
          domain.shortCode &&
          activeCodes.has(domain.code.toLowerCase())
      )
      .map((domain) => ({
        code: domain.code,
        shortCode: domain.shortCode as string,
        label: getOrganizationTypeLabel(domain.code),
      }));
  }

  render() {
    const badges = this.badges;
    if (badges.length === 0) return nothing;

    return html`
      <div class="operation-type-badges">
        ${badges.map(
          (badge) => html`
            <sl-tooltip content=${badge.label}>
              <span class="operation-type-badge">${badge.shortCode}</span>
            </sl-tooltip>
          `
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'site-operation-type-badges': SiteOperationTypeBadges;
  }
}
